import React, { PureComponent } from 'react';
import { Field, FieldProps } from 'formik';
import { FieldValueUpdate } from '../util/Survey';
import {
  RepeatingGroupInstanceContext,
  withOnFieldUpdateContext,
  WithOnFieldUpdateInjectedProps,
} from './Contexts';

export interface SurveyFieldProps {
  name: string;
  NestedComponent: React.ComponentType<FieldProps<any> & any>;
  responseAnswerType: string;
}

/**
 * This component intercepts relevant formik field events and performs necessary operations on the survey response.
 * The additional functionality should not affect any of the default Formik Field behavior.
 */
class SurveyField<PassThroughProps extends any> extends PureComponent<
  SurveyFieldProps &
    FieldProps<any> &
    WithOnFieldUpdateInjectedProps &
    PassThroughProps
> {
  /**
   * The whole response may not be represented in a single form.  The form state must be mirrored back into the survey response
   * for executing response wide validation and other operations
   *
   * @memberof SurveyField
   */
  handleSetFieldValue = (fieldLink: string, value: any, ...rest: any) => {
    const {
      form: { setFieldValue },
      responseAnswerType,
      onFieldUpdate,
    } = this.props;

    // update the survey response
    const updates = [
      new FieldValueUpdate(fieldLink, responseAnswerType as any, value),
    ];

    onFieldUpdate(updates);

    // execute the default formik behavior
    setFieldValue(fieldLink, value, ...rest);
  };

  handleOnChange = (event: any) => {
    const warning = `
      It is assumed survey fields will use custom components and custom change handlers.
      This change will not be reflected in the survey response.  If this is not the intent, use form.setFieldValue to update the forms field value.
    `;
    console.warn(warning);
    this.props.field.onChange(event);
  };

  render() {
    const {
      field,
      form,
      NestedComponent,
      responseAnswerType,
      onFieldUpdate,
      name,
      ...toPass
    }: any = this.props;

    // intercept field updates
    const interceptedFormikProps = {
      field: {
        ...field,
        onChange: this.handleOnChange,
      },
      form: {
        ...form,
        setFieldValue: this.handleSetFieldValue,
      },
    } as FieldProps<any>;

    return (
      <NestedComponent
        {...interceptedFormikProps}
        {...(toPass as PassThroughProps as object)}
      />
    );
  }
}

const SurveyFieldWithHOC = withOnFieldUpdateContext(SurveyField as any);

export interface FormikFieldProps {
  name: string;
  component: React.ComponentType<FieldProps<any> & any>;
  responseAnswerType: string;
}

class FormikField<PassThroughProps extends {}> extends PureComponent<
  FormikFieldProps & PassThroughProps
> {
  static contextType = RepeatingGroupInstanceContext;
  declare context: React.ContextType<typeof RepeatingGroupInstanceContext>;

  render() {
    const { name, component, responseAnswerType, ...toPass } = this.props;

    // fields inside a repeating group instance get instance-qualified names
    // (e.g. "sisters[1].sister-age") so repeated linkIds don't collide
    const qualifiedName = this.context
      ? `${this.context.namePrefix}${name}`
      : name;

    return (
      <Field
        name={qualifiedName}
        component={SurveyFieldWithHOC}
        NestedComponent={component}
        responseAnswerType={responseAnswerType}
        {...(toPass as PassThroughProps)}
      />
    );
  }
}

export default FormikField;
